import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Red panda — a storybook red panda standing on all fours, its big ringed tail
// curling up behind it. Chunky toy proportions: an oversized head with white
// face markings and cream-tipped ears on a plump rust body over stubby dark
// legs. Built to animate: the head and the whole ringed tail are named so the
// "tail-swish" loop can sweep the tail side to side while the idle keeps the
// little body breathing.
//
// Frame: faces +Z, stands on the floor (y = 0). About 0.4 m tall at the ears,
// the tail reaching back along -Z.
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body" : the torso. Breathes.
//   - "head" : the whole face (ears, markings, eyes, muzzle). Pivot at the
//              neck so bobs and tilts read on the shoulders, not the floor.
//   - "tail" : every ring of the tail. Pivot at the tail root, so a swish
//              sweeps the whole tail like a rudder.
// The legs are static (unnamed) and merge into the base mesh — planted.

const FUR = "#c1502e";         // rust-red fur, warm and saturated
const FUR_DARK = "#933a1f";    // shaded rust for the tail's dark tip and shading
const LIMB = "#4a342c";        // the dark chocolate legs and belly
const CREAM = "#f6efe3";       // white face markings, ear tips, tail rings
const NOSE = "#3a2c28";        // the little nose — deep brown, not black
const EYE_WHITE = "#faf6ec";   // eye whites
const PUPIL = "#33282e";       // big friendly pupils

// The pivots the animations rotate the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0.20, -0.03];
const NECK_PIVOT: [number, number, number] = [0, 0.27, 0.08];
const TAIL_PIVOT: [number, number, number] = [0, 0.21, -0.19];

// One big friendly eye: white ball, big dark pupil slightly forward, catch-light.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.027, 10, 8), color: EYE_WHITE,
      position: [x, 0.335, 0.195], name: "head" },
    { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: PUPIL,
      position: [x * 0.96, 0.333, 0.213], name: "head" },
    { geometry: new THREE.SphereGeometry(0.006, 6, 5), color: EYE_WHITE,
      position: [x * 0.96 + 0.007, 0.341, 0.226], name: "head" },
  ];
}

// One perky ear: a rust cone with a cream tip — the red panda's signature.
function ear(x: number): Part[] {
  return [
    { geometry: new THREE.ConeGeometry(0.038, 0.065, 6), color: FUR,
      position: [x, 0.415, 0.075], rotation: [0, 0, -Math.sign(x) * 0.35], name: "head" },
    { geometry: new THREE.SphereGeometry(0.016, 6, 5), color: CREAM,
      position: [x * 1.13, 0.443, 0.075], name: "head" },
  ];
}

// One stubby standing leg: a dark column with a rounded paw at the floor.
function leg(x: number, z: number): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.032, 0.036, 0.13, 8), color: LIMB,
      position: [x, 0.065, z] },
    { geometry: new THREE.SphereGeometry(0.04, 8, 6), color: LIMB,
      position: [x, 0.032, z + 0.012], scale: [1.0, 0.8, 1.25] },
  ];
}

// The ringed tail: fat rust and cream rings marching back and curling up,
// ending in a dark tip. All named "tail" so they sweep as one.
function tail(): Part[] {
  const rings: Array<{ r: number; y: number; z: number; color: string }> = [
    { r: 0.056, y: 0.215, z: -0.235, color: FUR },
    { r: 0.053, y: 0.221, z: -0.287, color: CREAM },
    { r: 0.050, y: 0.230, z: -0.337, color: FUR },
    { r: 0.046, y: 0.242, z: -0.384, color: CREAM },
    { r: 0.042, y: 0.257, z: -0.427, color: FUR },
    { r: 0.037, y: 0.274, z: -0.465, color: CREAM },
    { r: 0.031, y: 0.292, z: -0.497, color: FUR_DARK },
  ];
  return rings.map(({ r, y, z, color }): Part => ({
    geometry: new THREE.SphereGeometry(r, 9, 7), color,
    position: [0, y, z], scale: [1.0, 1.0, 1.2], name: "tail",
  }));
}

export const redPanda = defineModel({
  id: "red-panda",
  batchId: "mess",
  name: "Red Panda",
  tags: ["mess", "cute", "animated", "creature", "animal", "red-panda", "mascot"],
  summary: "A plump red panda on all fours, swishing its big ringed tail.",
  description:
    "A storybook red panda standing on all fours: an oversized rust head with " +
    "white cheek and brow markings, cream-tipped ears and a deep-brown nose, a " +
    "plump body on stubby chocolate legs, and a big ringed rust-and-cream tail " +
    "curling up behind. The head and the whole tail are separately posable; " +
    "the tail-swish loop sweeps the tail side to side while the head peeks " +
    "around, and the idle keeps the little body breathing.",
  author: "models",
  footprint: 0.5,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── torso ──────────────────────────────────────────────────────────
      // Plump rust body, long along Z, riding on the legs.
      { geometry: new THREE.SphereGeometry(0.115, 12, 10), color: FUR,
        position: [0, 0.20, -0.03], scale: [1.0, 0.95, 1.4], name: "body" },
      // Dark chest and belly underside — a red panda wears its shadow below.
      { geometry: new THREE.SphereGeometry(0.09, 10, 8), color: LIMB,
        position: [0, 0.155, 0.0], scale: [0.95, 0.75, 1.35], name: "body" },
      // ── legs (planted, static) ─────────────────────────────────────────
      ...leg(-0.072, 0.095),
      ...leg(0.072, 0.095),
      ...leg(-0.072, -0.135),
      ...leg(0.072, -0.135),
      // ── head ───────────────────────────────────────────────────────────
      // The oversized round head, riding high at the front.
      { geometry: new THREE.SphereGeometry(0.105, 14, 12), color: FUR,
        position: [0, 0.315, 0.115], scale: [1.1, 0.95, 0.95], name: "head" },
      // White cheek fluffs.
      { geometry: new THREE.SphereGeometry(0.048, 8, 6), color: CREAM,
        position: [-0.078, 0.293, 0.175], scale: [1.0, 0.9, 0.75], name: "head" },
      { geometry: new THREE.SphereGeometry(0.048, 8, 6), color: CREAM,
        position: [0.078, 0.293, 0.175], scale: [1.0, 0.9, 0.75], name: "head" },
      // White brow patches above the eyes, laid flat against the fur.
      { geometry: new THREE.SphereGeometry(0.019, 8, 6), color: CREAM,
        position: [-0.046, 0.366, 0.178], scale: [1.0, 0.75, 0.45], name: "head" },
      { geometry: new THREE.SphereGeometry(0.019, 8, 6), color: CREAM,
        position: [0.046, 0.366, 0.178], scale: [1.0, 0.75, 0.45], name: "head" },
      // White muzzle with the deep-brown nose.
      { geometry: new THREE.SphereGeometry(0.05, 10, 8), color: CREAM,
        position: [0, 0.29, 0.2], scale: [1.05, 0.75, 0.75], name: "head" },
      { geometry: new THREE.SphereGeometry(0.02, 8, 6), color: NOSE,
        position: [0, 0.312, 0.238], scale: [1.2, 0.8, 0.8], name: "head" },
      ...eye(-0.054),
      ...eye(0.054),
      ...ear(-0.088),
      ...ear(0.088),
      // ── the ringed tail ────────────────────────────────────────────────
      ...tail(),
    ];
  },
  animations: [
    // 1. Idle — a slow breath: the body swells a touch, the head bobs and the
    //    tail tip drifts lazily. Never looks dead.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 3.4,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: 1, ease: "sine" },
            { t: 1.7, value: 1.03, ease: "sine" },
            { t: 3.4, value: 1, ease: "sine" },
          ],
        },
        {
          target: "head", pivot: NECK_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.7, value: [0, 0.012, 0], ease: "sine" },
            { t: 3.4, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.02, 0, 0.02], ease: "sine" },
            { t: 1.7, value: [-0.03, 0, -0.02], ease: "sine" },
            { t: 3.4, value: [0.02, 0, 0.02], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0.06, 0], ease: "sine" },
            { t: 1.7, value: [0.04, -0.06, 0], ease: "sine" },
            { t: 3.4, value: [0, 0.06, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Tail swish — the big ringed tail sweeps wide, left then right, with a
    //    happy lift at each end; the head peeks the opposite way for balance.
    {
      id: "tail-swish",
      label: "Tail swish",
      duration: 3.0,
      loop: true,
      tracks: [
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.75, value: [-0.15, 0.7, 0.1], ease: "easeInOut" },
            { t: 1.5, value: [0, 0, 0], ease: "easeInOut" },
            { t: 2.25, value: [-0.15, -0.7, -0.1], ease: "easeInOut" },
            { t: 3.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "head", pivot: NECK_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.75, value: [0, -0.22, 0.08], ease: "easeInOut" },
            { t: 1.5, value: [0, 0, 0], ease: "easeInOut" },
            { t: 2.25, value: [0, 0.22, -0.08], ease: "easeInOut" },
            { t: 3.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          // The body rocks a hair with the sweep.
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.75, value: [0, 0.05, 0.03], ease: "sine" },
            { t: 1.5, value: [0, 0, 0], ease: "sine" },
            { t: 2.25, value: [0, -0.05, -0.03], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
